// TanStack Query wrappers for the Voucher feature.

import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryResult,
} from '@tanstack/react-query';
import { useAuthStore } from '@/stores/use-auth-store';
import { vouchersApi } from './api';
import type {
  Voucher,
  VoucherCreateInput,
  VoucherListParams,
  VoucherListResult,
  VoucherRedemptionList,
  VoucherStats,
} from './types';

function useTenantId(): string {
  return useAuthStore((s) => s.profile?.tenant_id ?? 'anonymous');
}

export function useVoucherList(params: VoucherListParams): UseQueryResult<VoucherListResult> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [
      'vouchers',
      tenantId,
      'list',
      params.page ?? 1,
      params.search ?? '',
      params.status ?? '',
      params.type ?? '',
    ],
    queryFn: () => vouchersApi.list(params),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useVoucher(id: string | undefined): UseQueryResult<Voucher | null> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['vouchers', tenantId, 'detail', id],
    queryFn: () => (id ? vouchersApi.get(id) : Promise.resolve(null)),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useVoucherStats(id: string | undefined): UseQueryResult<VoucherStats> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['vouchers', tenantId, 'stats', id],
    queryFn: () => vouchersApi.stats(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

export function useVoucherRedemptions(
  id: string | undefined,
  page: number,
): UseQueryResult<VoucherRedemptionList> {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ['vouchers', tenantId, 'redemptions', id, page],
    queryFn: () => vouchersApi.redemptions(id!, page),
    enabled: Boolean(id),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}

export function useCreateVoucher() {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: (input: VoucherCreateInput) => vouchersApi.create(input),
    onSuccess: (created) => {
      qc.setQueryData(['vouchers', tenantId, 'detail', created.id], created);
      qc.invalidateQueries({ queryKey: ['vouchers', tenantId, 'list'] });
    },
  });
}

export function useDeactivateVoucher(id: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: () => vouchersApi.deactivate(id),
    onSuccess: (updated) => {
      qc.setQueryData(['vouchers', tenantId, 'detail', id], updated);
      qc.invalidateQueries({ queryKey: ['vouchers', tenantId, 'list'] });
    },
  });
}

export function useDeleteVoucher(id: string) {
  const qc = useQueryClient();
  const tenantId = useTenantId();
  return useMutation({
    mutationFn: () => vouchersApi.remove(id),
    onSuccess: () => {
      qc.removeQueries({ queryKey: ['vouchers', tenantId, 'detail', id] });
      qc.invalidateQueries({ queryKey: ['vouchers', tenantId, 'list'] });
    },
  });
}
